import type { Workspace } from '@noconflict/contracts';
import { z } from 'zod';
import type { OrgScopeData } from './scope';

const STORAGE_KEY = 'ncc.lastWorkspace';
const stored = z.record(z.string(), z.string().min(1));

function readAll(): Record<string, string> {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = stored.safeParse(JSON.parse(raw));
    return parsed.success ? parsed.data : {};
  } catch {
    return {};
  }
}

/** The workspace the person last opened in an org. A convenience for "Back to projects" only. */
export function readLastWorkspaceSlug(orgSlug: string): string | null {
  return readAll()[orgSlug] ?? null;
}

export function writeLastWorkspaceSlug(orgSlug: string, workspaceSlug: string): void {
  try {
    window.localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ ...readAll(), [orgSlug]: workspaceSlug }),
    );
  } catch {
    // Storage blocked: "Back to projects" falls back to the first workspace.
  }
}

/** Where "Back to projects" goes: the last-used active workspace, else the first, else the org. */
export function projectsHome(
  org: { slug: string },
  activeWorkspaces: readonly Workspace[],
): string {
  const last = readLastWorkspaceSlug(org.slug);
  const target = activeWorkspaces.find((w) => w.slug === last) ?? activeWorkspaces[0];
  return target ? `/o/${org.slug}/w/${target.slug}` : `/o/${org.slug}`;
}

/**
 * Whether the Orders page is worth showing: the caller holds `projects.view` or `orders.pay` somewhere in the
 * org (by org role, or by a grant on a workspace). Display only: the view `customer_orders` decides the rows.
 */
export function canSeeOrders(
  scope: Pick<OrgScopeData, 'can' | 'canIn' | 'activeWorkspaces' | 'archivedWorkspaces'>,
): boolean {
  return (
    scope.can('projects.view') ||
    scope.can('orders.pay') ||
    [...scope.activeWorkspaces, ...scope.archivedWorkspaces].some(
      (w) => scope.canIn(w.id, 'projects.view') || scope.canIn(w.id, 'orders.pay'),
    )
  );
}

export const SETTINGS_SECTIONS = ['general', 'members', 'workspaces', 'billing'] as const;
export type SettingsSection = (typeof SETTINGS_SECTIONS)[number];

/**
 * The org settings sections worth showing the caller. General is for every member (leaving is there);
 * Workspaces for someone who manages workspaces or at least one workspace. Display only: the RPCs and
 * RLS refuse the rest regardless.
 */
export function visibleSettingsSections(
  scope: Pick<OrgScopeData, 'can' | 'canIn' | 'activeWorkspaces' | 'archivedWorkspaces'>,
): SettingsSection[] {
  const managesAWorkspace = [...scope.activeWorkspaces, ...scope.archivedWorkspaces].some((w) =>
    scope.canIn(w.id, 'workspace.manage'),
  );
  return SETTINGS_SECTIONS.filter((section) => {
    if (section === 'members') return scope.can('members.manage');
    if (section === 'workspaces') return scope.can('workspaces.manage') || managesAWorkspace;
    if (section === 'billing') return scope.can('billing.view');
    return true;
  });
}
